import { motion, AnimatePresence } from 'framer-motion'
import { useState, useMemo } from 'react'
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isToday, isSameDay,
  addMonths, subMonths, isWeekend
} from 'date-fns'
import { ChevronLeft, ChevronRight, Clock, Users, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EventType } from './mockEventData'
import { cn } from '@/lib/utils'

interface CalendarGridProps {
  events: EventType[]
  selectedDate?: Date
  onDateSelect?: (date: Date) => void
  onEventClick?: (event: EventType) => void
  className?: string
}

interface EventsByDate {
  [key: string]: EventType[]
}

export function CalendarGrid({
  events,
  selectedDate,
  onDateSelect,
  onEventClick,
  className
}: CalendarGridProps) {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date())
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)

  // Group events by date
  const eventsByDate: EventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const dateKey = format(event.startTime, 'yyyy-MM-dd')
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(event)
      return acc
    }, {} as EventsByDate)
  }, [events])

  // Calculate calendar days
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentDate(prev => direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1))

    setTimeout(() => setIsAnimating(false), 400)
  }

  const getDayEvents = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return eventsByDate[dateKey] || []
  }

  const getEventTypeColor = (type: EventType['type']) => {
    const colors = {
      demo: 'from-blue-500 to-cyan-500',
      webinar: 'from-purple-500 to-pink-500',
      workshop: 'from-orange-500 to-red-500',
      tour: 'from-green-500 to-teal-500',
      launch: 'from-yellow-400 to-orange-500'
    }
    return colors[type] || 'from-gray-500 to-gray-600'
  }

  const calendarVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    })
  }

  return (
    <div className={cn("w-full max-w-6xl mx-auto", className)}>
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-8">
        <motion.h2
          key={format(currentDate, 'MMMM yyyy')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-3xl font-bold text-gray-900 dark:text-white"
        >
          {format(currentDate, 'MMMM yyyy')}
        </motion.h2>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateMonth('prev')}
            disabled={isAnimating}
            className="hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-300 dark:border-gray-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date())}
            className="px-4 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-300 dark:border-gray-600"
          >
            Today
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateMonth('next')}
            disabled={isAnimating}
            className="hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-300 dark:border-gray-600"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
          {weekDays.map((day) => (
            <div key={day} className="p-4 text-center">
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                {day}
              </span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" custom={isAnimating}>
            <motion.div
              key={format(currentDate, 'yyyy-MM')}
              custom={isAnimating ? 1 : 0}
              variants={calendarVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="grid grid-cols-7"
            >
              {calendarDays.map((day, index) => {
                const dayEvents = getDayEvents(day)
                const isCurrentMonth = isSameMonth(day, currentDate)
                const isTodayDate = isToday(day)
                const isSelected = selectedDate && isSameDay(day, selectedDate)
                const isHovered = hoveredDate && isSameDay(day, hoveredDate)
                const hasEvents = dayEvents.length > 0
                const isWeekendDay = isWeekend(day)

                return (
                  <motion.div
                    key={day.toISOString()}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.01, duration: 0.3 }}
                    className={cn(
                      "relative min-h-[120px] p-3 border-r border-b border-gray-100 dark:border-gray-700 cursor-pointer group transition-colors duration-300",
                      !isCurrentMonth && "opacity-40 bg-gray-50/50 dark:bg-gray-800/50",
                      isCurrentMonth && "hover:bg-blue-50/50 dark:hover:bg-blue-900/10",
                      isTodayDate && "bg-blue-50 dark:bg-blue-900/20",
                      isSelected && "bg-blue-100 dark:bg-blue-900/30",
                      isHovered && "bg-blue-50 dark:bg-blue-900/15",
                      isWeekendDay && isCurrentMonth && "bg-gray-50/30 dark:bg-gray-800/30"
                    )}
                    onClick={() => {
                      onDateSelect?.(day)
                      setCurrentDate(day)
                    }}
                    onMouseEnter={() => setHoveredDate(day)}
                    onMouseLeave={() => setHoveredDate(null)}
                  >
                    {/* Date Number */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className={cn(
                        "w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold mb-2 transition-colors transition-shadow",
                        isTodayDate && "bg-blue-500 text-white shadow-lg",
                        !isTodayDate && isCurrentMonth && "text-gray-900 dark:text-white group-hover:bg-blue-500 group-hover:text-white",
                        !isCurrentMonth && "text-gray-400 dark:text-gray-600"
                      )}
                    >
                      {format(day, 'd')}
                    </motion.div>

                    {/* Events */}
                    {hasEvents && (
                      <div className="space-y-1">
                        {/* Event dots */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {dayEvents.slice(0, 3).map((event, eventIndex) => (
                            <motion.div
                              key={event.id}
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: index * 0.01 + eventIndex * 0.05 }}
                              className={cn(
                                "w-2 h-2 rounded-full cursor-pointer hover:scale-150 transition-transform bg-gradient-to-r",
                                getEventTypeColor(event.type)
                              )}
                              onClick={(e) => {
                                e.stopPropagation()
                                onEventClick?.(event)
                              }}
                              title={event.title}
                            />
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              +{dayEvents.length - 3}
                            </div>
                          )}
                        </div>

                        {/* First event preview */}
                        {dayEvents[0] && (
                          <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.01 + 0.1 }}
                            className="text-xs text-gray-600 dark:text-gray-300 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEventClick?.(dayEvents[0])
                            }}
                          >
                            {format(dayEvents[0].startTime, 'HH:mm')} {dayEvents[0].title}
                          </motion.div>
                        )}

                        {/* Additional events count */}
                        {dayEvents.length > 1 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {dayEvents.length - 1} more event{dayEvents.length > 2 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Hover overlay with event preview */}
                    <AnimatePresence>
                      {isHovered && hasEvents && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.9 }}
                          className="absolute bottom-full left-0 right-0 z-50 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3"
                        >
                          <div className="space-y-2">
                            {dayEvents.slice(0, 3).map((event) => (
                              <div key={event.id} className="flex items-start gap-2">
                                <div className={cn(
                                  "w-2 h-2 rounded-full mt-1.5 bg-gradient-to-r flex-shrink-0",
                                  getEventTypeColor(event.type)
                                )} />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {event.title}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                    <Clock className="w-3 h-3" />
                                    {format(event.startTime, 'HH:mm')}
                                    <Users className="w-3 h-3" />
                                    {event.currentAttendees}/{event.maxAttendees}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1 border-t border-gray-200 dark:border-gray-600">
                                +{dayEvents.length - 3} more events
                              </div>
                            )}
                          </div>

                          {/* Arrow pointer */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white dark:border-t-gray-800" />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Empty day add event button */}
                    {isCurrentMonth && !hasEvents && isHovered && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center hover:bg-blue-500 hover:text-white transition-colors">
                          <Plus className="w-3 h-3" />
                        </div>
                      </motion.div>
                    )}

                    {/* Selection ring */}
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none"
                      />
                    )}
                  </motion.div>
                )
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Calendar Legend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-6 flex flex-wrap gap-6 justify-center text-sm"
      >
        {[
          { label: 'Demo', type: 'demo' as const },
          { label: 'Webinar', type: 'webinar' as const },
          { label: 'Workshop', type: 'workshop' as const },
          { label: 'Tour', type: 'tour' as const },
          { label: 'Launch', type: 'launch' as const }
        ].map(({ label, type }) => (
          <div key={type} className="flex items-center gap-2">
            <div className={cn(
              "w-3 h-3 rounded-full bg-gradient-to-r",
              getEventTypeColor(type)
            )} />
            <span className="text-gray-600 dark:text-gray-400">{label}</span>
          </div>
        ))}
      </motion.div>
    </div>
  )
}